/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Configuration, LogLevel } from '@azure/msal-browser'
import { AUTHORITY, AUTHORITY_DOMAIN, CLIENT_ID } from './app/axios/const'
import logger from './app/middleware/log'

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const b2cPolicies = {
  names: {
    signIn: 'B2C_1A_signup_signin',
    signUpLocal: 'B2C_1A_SIGNUP_LOCAL',
    signUp365: 'B2C_1A_SIGNUP365',
  },
  authorities: {
    signIn: {
      authority: AUTHORITY,
    },
  },
  authorityDomain: AUTHORITY_DOMAIN,
}

export interface AuthError extends Error {
  // This is a short code describing the error
  errorCode: string
  // This is a descriptive string of the error,
  // and may also contain the mitigation strategy
  errorMessage: string
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: b2cPolicies.authorities.signIn.authority,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii === true) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            logger.error(message)
            return
          case LogLevel.Info:
            logger.info(message)
            return
          case LogLevel.Verbose:
            logger.debug(message)
            return
          case LogLevel.Warning:
            logger.warn(message)
        }
      },
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['openid', 'offline_access', CLIENT_ID],
  state: window.location.href,
  extraQueryParameters: { domainUrl: window.location.origin },
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}
